import { useEffect, useRef, useState } from 'react';
import { useForgeUiAnalyticsEvent } from '../../analytics/useForgeUiAnalyticsEvent';
import { type ForgeUIExtensionType } from '@atlassian/forge-ui-types';
import { useHeightObserver } from './useHeightObserver';

const getResizeTotal = (resizes: number[]) => {
	return resizes.reduce((total, current, index) => {
		if (index === 0) {
			return 0;
		}
		return total + Math.abs(current - resizes[index - 1]);
	}, 0);
};

const useEmitResizeAnalytics = ({
	isEnabled,
	appType,
	extensionType,
	forgeEnvironment,
	resizedHeightValues,
}: {
	isEnabled: boolean;
	appType: 'UIKit' | 'CustomUI';
	extensionType: ForgeUIExtensionType['type'];
	forgeEnvironment: ForgeUIExtensionType['environmentType'];
	resizedHeightValues: Array<{ height: number; timestamp: string }>;
}) => {
	const { trackExtensionResized } = useForgeUiAnalyticsEvent();

	const [isTrackingResize, setIsTrackingResize] = useState(isEnabled);

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | null = null;

		if (isTrackingResize) {
			// Collect resizes within the first 5 seconds of the app loading
			timeoutId = setTimeout(() => {
				setIsTrackingResize(false);
				if (resizedHeightValues.length > 0) {
					const resizeTotal = getResizeTotal(resizedHeightValues.map(({ height }) => height));
					if (resizeTotal > 0) {
						trackExtensionResized({
							appType,
							extensionType,
							resizeTotal,
							resizedValues: resizedHeightValues,
							forgeEnvironment,
						});
					}
				}
			}, 5000);
		}
		return () => {
			// Clear timeout if extension is removed before the analytic was sent
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [
		appType,
		extensionType,
		forgeEnvironment,
		isEnabled,
		isTrackingResize,
		resizedHeightValues,
		trackExtensionResized,
	]);

	return { isTrackingResize };
};

export const useResizeAnalytics = ({
	isEnabled,
	trackedAttributeValues: { appType, extensionType, forgeEnvironment },
}: {
	isEnabled: boolean;
	trackedAttributeValues: {
		appType: 'UIKit' | 'CustomUI';
		extensionType: ForgeUIExtensionType['type'];
		forgeEnvironment: ForgeUIExtensionType['environmentType'];
	};
}) => {
	const { containerRef, height } = useHeightObserver();
	const resizedHeightValues = useRef<Array<{ height: number; timestamp: string }>>([]);

	const { isTrackingResize } = useEmitResizeAnalytics({
		isEnabled,
		appType,
		extensionType,
		forgeEnvironment,
		resizedHeightValues: resizedHeightValues.current,
	});

	useEffect(() => {
		if (height !== null && isTrackingResize && isEnabled) {
			resizedHeightValues.current.push({
				height,
				timestamp: new Date().toUTCString(),
			});
		}
	}, [height, isTrackingResize, isEnabled]);

	return { containerRef };
};
